import { useState } from "react";
import {
  AccessQuery,
  DealGroupQuery,
  useUpdateDealAccountMutation,
  DealAccountFragment,
} from "../../graphql/generated";
import { useQueryClient } from "@tanstack/react-query";
import useGqlClient from "../../hooks/useGqlClient";
import { AnimatedModal } from "../../components/AnimatedModal";
import { H4 } from "../../components/Heading";
import { CloseIcon } from "../../components/CloseIcon";
import { Button } from "../../components/tailwind/Button";
import Dropdown, { Option } from "../../components/tailwind/Dropdown";
import { formatDistanceToNow, fromUnixTime } from "date-fns";
import {
  DealRoleOption,
  DealRolesDropdown,
} from "@/src/components/permissions/DealRolesDropdown";

export function EditDealAccount(props: {
  dealGroup?: DealGroupQuery["dealGroup"];
  groups: AccessQuery["deal"]["guestGroups"];
  dealAccount: DealAccountFragment | null;
  permissions: AccessQuery["deal"]["dataRoom"]["permissions"];
  open: boolean;
  onClose: () => void;
  dealId: string;
}) {
  if (!props.dealAccount) {
    return null;
  }

  return (
    <EditDealAccountContent
      dealGroup={props.dealGroup}
      groups={props.groups}
      dealAccount={props.dealAccount}
      permissions={props.permissions}
      open={props.open}
      onClose={props.onClose}
      dealId={props.dealId}
    />
  );
}

function EditDealAccountContent(props: {
  dealGroup?: DealGroupQuery["dealGroup"];
  groups: AccessQuery["deal"]["guestGroups"];
  dealAccount: DealAccountFragment;
  permissions: AccessQuery["deal"]["dataRoom"]["permissions"];
  open: boolean;
  onClose: () => void;
  dealId: string;
}) {
  const queryClient = useQueryClient();
  const client = useGqlClient();

  const [selectedRoleId, setSelectedRoleId] = useState(
    props.dealAccount.dealRole.id,
  );

  const [dealGroup, setDealGroup] = useState<Option | null>(
    props.dealGroup
      ? {
          label: props.dealGroup.name,
          value: props.dealGroup.id,
          description: `${props.dealGroup.accounts.length} ${
            props.dealGroup.accounts.length === 1 ? "member" : "members"
          }`,
        }
      : null,
  );

  const updateDealAccount = useUpdateDealAccountMutation(client);

  async function onSave() {
    await updateDealAccount.mutateAsync({
      input: {
        id: props.dealAccount.id,
        roleID: selectedRoleId,
        ...(dealGroup && dealGroup.value !== props.dealGroup?.id
          ? { dealGroupID: dealGroup.value as string }
          : {}),
      },
    });

    queryClient.invalidateQueries({
      queryKey: ["Access", { dealId: props.dealId }],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "DealAccount",
        { id: props.dealAccount.id, dealId: props.dealId },
      ],
    });
    props.onClose();
  }

  return (
    <AnimatedModal
      open={props.open}
      onClose={() => {
        props.onClose();
      }}
      size="lg"
    >
      <div>
        <div className="flex items-center justify-between">
          <H4 margin="0">Edit {props.dealAccount.account.name}</H4>
          <CloseIcon onClose={props.onClose} />
        </div>
        <p className="text-sm text-gray-500/80 leading-tight">
          Joined{" "}
          {formatDistanceToNow(fromUnixTime(props.dealAccount.createdAt), {
            addSuffix: true,
          })}
        </p>
        <div className="mt-4">
          <div className="mt-2">
            <DealRolesDropdown
              dealId={props.dealId}
              roleType={props.dealAccount.dealRole.type}
              value={selectedRoleId}
              onChange={(role: DealRoleOption) => setSelectedRoleId(role.id)}
              label="Role"
            />
          </div>
          <div className="flex flex-col mt-4">
            <p className="block text-sm font-semibold leading-6 text-gray-900">
              Deal group
            </p>
            <Dropdown
              options={props.groups.map((g) => ({
                label: g.name,
                value: g.id,
                description: `${g.accounts.length} ${
                  g.accounts.length === 1 ? "member" : "members"
                }`,
              }))}
              onSelect={(o) => {
                setDealGroup(o);
              }}
              selectedOption={dealGroup || undefined}
            />
          </div>
        </div>

        <div className="w-full h-px bg-gray-200 my-4"></div>
        <div className="flex justify-end mt-5">
          <Button
            text="Save"
            variant="positive"
            isLoading={updateDealAccount.isPending}
            loadingText="Saving..."
            onClick={onSave}
          />
        </div>
      </div>
    </AnimatedModal>
  );
}
